import React, { useState, useEffect } from 'react';
import { Save, LogOut, Edit, Eye, EyeOff, Building2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { logout, updateUser, updateBusiness } from '../services/api';
import { uploadImageToCloudinary } from '../services/cloudinary';
import { useLanguage } from '../contexts/LanguageContext';
import ImageSourcePicker from '../components/ImageSourcePicker';

const Profile = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [businessData, setBusinessData] = useState({
    businessName: '',
    businessAddress: '',
    contactNumber: '',
    profileUrl: '',
    logo: null
  });
  const [logoPreview, setLogoPreview] = useState(null);
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);

  const [userData, setUserData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    role: ''
  });

  const [isEditMode, setIsEditMode] = useState(false);
  const [isBusinessEditMode, setIsBusinessEditMode] = useState(false);
  const [passwordData, setPasswordData] = useState({
    previousPassword: '',
    newPassword: ''
  });
  const [showPreviousPassword, setShowPreviousPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isBusinessLoading, setIsBusinessLoading] = useState(false);
  const [error, setError] = useState('');
  const [businessError, setBusinessError] = useState('');
  const [userId, setUserId] = useState(null);
  const [businessId, setBusinessId] = useState(null);

  useEffect(() => {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const businesses = JSON.parse(localStorage.getItem('businesses') || '[]');
      const selected = JSON.parse(localStorage.getItem('selectedBusiness') || 'null');
      const biz = selected || businesses[0] || {};

      const currentUserId = user.userId || user.id;
      setUserId(currentUserId);

      setUserData({
        firstName: user.firstName || user.first_name || '',
        lastName: user.lastName || user.last_name || '',
        email: user.email || '',
        role: user.role || ''
      });

      const currentBusinessId = biz.id;
      setBusinessId(currentBusinessId);

      const profileUrl = biz.profileUrl || '';
      setBusinessData({
        businessName: biz.businessName || '',
        businessAddress: biz.address || '',
        contactNumber: biz.contactNumber || '',
        profileUrl: profileUrl,
        logo: null
      });
      setLogoPreview(profileUrl);
    } catch (_) {}
  }, []);

  const handleBusinessChange = (e) => {
    const { name, value } = e.target;
    setBusinessData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleUserChange = (e) => {
    const { name, value } = e.target;
    setUserData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
  };

  const toggleEditMode = () => {
    setIsEditMode(!isEditMode);
    if (isEditMode) {
      // Reset password fields when canceling edit mode
      setPasswordData({
        previousPassword: '',
        newPassword: ''
      });
      setError('');
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setBusinessError(t('profile.errors.invalidImage'));
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setBusinessError(t('profile.errors.imageSizeLimit'));
      return;
    }

    setIsUploadingLogo(true);
    setBusinessError('');

    try {
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result);
      };
      reader.readAsDataURL(file);

      // Upload to Cloudinary
      const uploadedUrl = await uploadImageToCloudinary(file, { folder: 'business-logos' });
      
      // Update with Cloudinary URL for preview
      setLogoPreview(uploadedUrl);
      
      setBusinessData(prev => ({
        ...prev,
        profileUrl: uploadedUrl,
        logo: file
      }));
    } catch (uploadError) {
      setBusinessError(`${t('profile.errors.uploadFailed')}: ${uploadError.message}`);
      setLogoPreview(null);
    } finally {
      setIsUploadingLogo(false);
    }
  };

  const toggleBusinessEditMode = () => {
    setIsBusinessEditMode(!isBusinessEditMode);
    if (isBusinessEditMode) {
      // Reset to original values when canceling
      try {
        const businesses = JSON.parse(localStorage.getItem('businesses') || '[]');
        const selected = JSON.parse(localStorage.getItem('selectedBusiness') || 'null');
        const biz = selected || businesses[0] || {};
        
        const profileUrl = biz.profileUrl || '';
        setBusinessData({
          businessName: biz.businessName || '',
          businessAddress: biz.address || '',
          contactNumber: biz.contactNumber || '',
          profileUrl: profileUrl,
          logo: null
        });
        setLogoPreview(profileUrl);
        setBusinessError('');
      } catch (_) {}
    }
  };

  const handleSaveBusinessProfile = async () => {
    if (!businessId) {
      setBusinessError(t('profile.errors.businessIdNotFound'));
      return;
    }

    // Validate required fields
    if (!businessData.contactNumber.trim()) {
      setBusinessError(t('profile.errors.contactRequired'));
      return;
    }

    if (!businessData.businessAddress.trim()) {
      setBusinessError(t('profile.errors.addressRequired'));
      return;
    }

    setIsBusinessLoading(true);
    setBusinessError('');

    try {
      const updateData = {
        businessName: businessData.businessName.trim(), // Include businessName for API but it's not editable
        contactNumber: businessData.contactNumber.trim(),
        address: businessData.businessAddress.trim(),
        profileUrl: businessData.profileUrl || ''
      };

      const response = await updateBusiness(businessId, updateData);

      if (response.success) {
        // Update local storage with new business data
        const businesses = JSON.parse(localStorage.getItem('businesses') || '[]');
        const selected = JSON.parse(localStorage.getItem('selectedBusiness') || 'null');
        
        const updatedBusinessData = { ...response.data };
        const updatedBusinesses = businesses.map(biz => 
          biz.id === businessId ? { ...biz, ...updatedBusinessData } : biz
        );
        localStorage.setItem('businesses', JSON.stringify(updatedBusinesses));
        
        if (selected && selected.id === businessId) {
          localStorage.setItem('selectedBusiness', JSON.stringify({ ...selected, ...updatedBusinessData }));
        }

        // Update logo preview with the saved profileUrl
        if (updatedBusinessData.profileUrl) {
          setLogoPreview(updatedBusinessData.profileUrl);
        }

        // Dispatch event to notify other components
        window.dispatchEvent(new Event('localStorageUpdated'));

        setIsBusinessEditMode(false);
      } else {
        setBusinessError(response.message || t('profile.errors.updateBusinessFailed'));
      }
    } catch (err) {
      setBusinessError(t('profile.errors.updateBusinessError'));
      console.error('Error updating business profile:', err);
    } finally {
      setIsBusinessLoading(false);
    }
  };

  const handleSaveUserProfile = async () => {
    if (!userId) {
      setError(t('profile.errors.userIdNotFound'));
      return;
    }

    // Validate password fields if user is trying to change password
    const isChangingPassword = passwordData.previousPassword || passwordData.newPassword;
    if (isChangingPassword) {
      if (!passwordData.previousPassword || !passwordData.newPassword) {
        setError(t('profile.errors.passwordFieldsRequired'));
        return;
      }
      if (passwordData.newPassword.length < 6) {
        setError(t('profile.errors.passwordMinLength'));
        return;
      }
    }

    setIsLoading(true);
    setError('');

    try {
      const updateData = {
        firstName: userData.firstName,
        lastName: userData.lastName,
        email: userData.email,
        role: userData.role
      };

      // Only include password if user is changing it
      if (isChangingPassword) {
        updateData.password = passwordData.newPassword;
      }

      const response = await updateUser(userId, updateData);

      if (response.success) {
        // Update local storage with new user data
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        const updatedUser = {
          ...user,
          firstName: response.data.firstName,
          lastName: response.data.lastName,
          email: response.data.email,
          role: response.data.role
        };
        localStorage.setItem('user', JSON.stringify(updatedUser));

        // If password was changed, redirect to login
        if (isChangingPassword) {
          await logout();
          navigate('/login?passwordChanged=success');
        } else {
          // Reset edit mode if only name was changed
          setIsEditMode(false);
          setPasswordData({
            previousPassword: '',
            newPassword: ''
          });
        }
      } else {
        setError(response.message || t('profile.errors.updateProfileFailed'));
      }
    } catch (err) {
      setError(t('profile.errors.updateProfileError'));
      console.error('Error updating user profile:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login?logout=success');
  };

  return (
    <div className="profile-page">
      {/* Business Profile Section */}
      <div className="card" style={{ marginBottom: '2rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
          <h2 className="card-title" style={{ margin: 0 }}>{t('profile.businessProfile')}</h2>
          {!isBusinessEditMode && (
            <button className="btn btn-primary" onClick={toggleBusinessEditMode}>
              <Edit size={16} />
              {t('profile.editBusinessProfile')}
            </button>
          )}
        </div>

        {businessError && (
          <div className="profile-alert-error">
            {businessError}
          </div>
        )}

        {/* Business Logo and Info Row */}
        <div className="profile-biz-row">
          {/* Logo/Icon Section */}
          <div className="profile-logo-col">
            <div
              className="profile-logo-frame"
              style={{ marginBottom: isBusinessEditMode ? '1rem' : '0' }}
            >
              {logoPreview ? (
                <img 
                  key={logoPreview}
                  src={logoPreview} 
                  alt="Business Logo" 
                  style={{ 
                    maxWidth: '100%', 
                    maxHeight: '100%', 
                    objectFit: 'contain',
                    borderRadius: '0.5rem',
                    display: 'block'
                  }}
                  onError={(e) => {
                    e.target.style.display = 'none';
                    const iconDiv = e.target.parentElement.querySelector('.business-icon-placeholder');
                    if (iconDiv) {
                      iconDiv.style.display = 'flex';
                    }
                  }}
                />
              ) : (
                <div className="business-icon-placeholder profile-logo-placeholder">
                  <Building2 size={64} />
                  <span style={{ marginTop: '0.5rem', fontSize: '0.875rem' }}>{t('profile.noLogo')}</span>
                </div>
              )}
            </div>

            {isBusinessEditMode && (
              <div style={{ width: '100%' }}>
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}
                >
                  <ImageSourcePicker
                    disabled={isUploadingLogo}
                    onChange={handleLogoUpload}
                    galleryLabel={isUploadingLogo ? t('profile.uploading') : (logoPreview ? t('profile.change') : t('common.gallery'))}
                    cameraLabel={t('common.camera')}
                    hint={t('profile.imageFormat')}
                  />
                </div>
              </div>
            )}
          </div>

          {/* Business Name and Contact Number Section */}
          <div className="profile-biz-fields">
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label">{t('profile.businessName')}</label>
              <input
                type="text"
                name="businessName"
                className="form-input"
                value={businessData.businessName}
                onChange={handleBusinessChange}
                disabled
              />
            </div>

            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label">{t('profile.contactNumber')}</label>
              <input
                type="tel"
                name="contactNumber"
                className="form-input"
                value={businessData.contactNumber}
                onChange={handleBusinessChange}
                disabled={!isBusinessEditMode}
              />
            </div>
          </div>
        </div>

        {/* Business Address - Full Width */}
        <div className="form-group" style={{ marginBottom: '2rem' }}>
          <label className="form-label">{t('profile.businessAddress')}</label>
          <textarea
            name="businessAddress"
            className="form-textarea"
            value={businessData.businessAddress}
            onChange={handleBusinessChange}
            rows={3}
            disabled={!isBusinessEditMode}
          />
        </div>

        {isBusinessEditMode && (
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem' }}>
            <button 
              className="btn btn-secondary" 
              onClick={toggleBusinessEditMode}
              disabled={isBusinessLoading}
            >
              {t('profile.cancel')}
            </button>
            <button 
              className="btn btn-primary" 
              onClick={handleSaveBusinessProfile}
              disabled={isBusinessLoading}
            >
              <Save size={16} />
              {isBusinessLoading ? t('profile.saving') : t('profile.saveBusinessProfile')}
            </button>
          </div>
        )}
      </div>

      {/* User Profile Section */}
      <div className="card" style={{ marginBottom: '2rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
          <h2 className="card-title" style={{ margin: 0 }}>{t('profile.userProfile')}</h2>
          {!isEditMode && (
            <button className="btn btn-primary" onClick={toggleEditMode}>
              <Edit size={16} />
              {t('profile.editProfile')}
            </button>
          )}
        </div>
        
        {error && (
          <div className="profile-alert-error">
            {error}
          </div>
        )}
        
        <div className="form-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem', marginBottom: '2rem' }}>
          <div className="form-group">
            <label className="form-label">{t('profile.firstName')}</label>
            <input
              type="text"
              name="firstName"
              className="form-input"
              value={userData.firstName}
              onChange={handleUserChange}
              disabled={!isEditMode}
            />
          </div>

          <div className="form-group">
            <label className="form-label">{t('profile.lastName')}</label>
            <input
              type="text"
              name="lastName"
              className="form-input"
              value={userData.lastName}
              onChange={handleUserChange}
              disabled={!isEditMode}
            />
          </div>
        </div>

        <div className="form-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem', marginBottom: '2rem' }}>
          <div className="form-group">
            <label className="form-label">{t('profile.email')}</label>
            <input
              type="email"
              name="email"
              className="form-input"
              value={userData.email}
              disabled
            />
          </div>

          <div className="form-group">
            <label className="form-label">{t('profile.role')}</label>
            <input
              type="text"
              name="role"
              className="form-input"
              value={userData.role}
              disabled
            />
          </div>
        </div>

        {isEditMode && (
          <div className="form-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem', marginBottom: '2rem' }}>
            <div className="form-group">
              <label className="form-label">{t('profile.previousPassword')}</label>
              <div style={{ position: 'relative' }}>
                <input
                  type={showPreviousPassword ? 'text' : 'password'}
                  name="previousPassword"
                  className="form-input"
                  value={passwordData.previousPassword}
                  onChange={handlePasswordChange}
                  placeholder={t('profile.enterCurrentPassword')}
                  style={{ paddingRight: '2.5rem' }}
                />
                <button
                  type="button"
                  onClick={() => setShowPreviousPassword(!showPreviousPassword)}
                  className="profile-password-toggle"
                >
                  {showPreviousPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">{t('profile.newPassword')}</label>
              <div style={{ position: 'relative' }}>
                <input
                  type={showNewPassword ? 'text' : 'password'}
                  name="newPassword"
                  className="form-input"
                  value={passwordData.newPassword}
                  onChange={handlePasswordChange}
                  placeholder={t('profile.enterNewPassword')}
                  style={{ paddingRight: '2.5rem' }}
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  className="profile-password-toggle"
                >
                  {showNewPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
          </div>
        )}

        {isEditMode && (
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem' }}>
            <button 
              className="btn btn-secondary" 
              onClick={toggleEditMode}
              disabled={isLoading}
            >
              {t('profile.cancel')}
            </button>
            <button 
              className="btn btn-primary" 
              onClick={handleSaveUserProfile}
              disabled={isLoading}
            >
              <Save size={16} />
              {isLoading ? t('profile.saving') : t('profile.saveChanges')}
            </button>
          </div>
        )}
      </div>

      {/* Logout Button */}
      <div style={{ display: 'flex', justifyContent: 'center' }}>
        <button 
          className="btn btn-logout-block"
          onClick={handleLogout}
        >
          <LogOut size={16} />
          {t('profile.logout')}
        </button>
      </div>
    </div>
  );
};

export default Profile;
